import { DisplayProjections } from 'src/app/model/response/log-display';

/** Limites do schema `display-projections.schema.json` (contrato §12.2). */
export const MAX_PROJECTED_CHANNELS = 10;
export const MAX_PROJECTION_MULTIPLIER = 1e6;
export const MAX_PROJECTION_OFFSET = 1e9;
export const MAX_PROJECTION_UNIT_LENGTH = 64;

type Named = { channel: string; unit: string | null };

/**
 * Resolve CADA canal entre o bloco canônico e o projetado (contrato §12.3):
 * `display` pode trazer só parte dos canais, então nunca se troca o bloco inteiro.
 * A ordem e o conjunto de canais são sempre os do canônico.
 */
export function preferByChannel<T extends Named>(canonical: T[], projected?: T[]): T[] {
  const display = new Map((projected ?? []).map((item) => [item.channel, item]));
  return canonical.map((item) => display.get(item.channel) ?? item);
}

/**
 * Lista os problemas de `projections` frente ao schema (chaves dentro dos canais
 * selecionados, até 10, módulos máximos, números finitos, unidade até 64 caracteres).
 * Vazio = válido.
 */
export function validateDisplayProjections(
  selectedChannels: readonly string[],
  projections: DisplayProjections,
): string[] {
  const issues: string[] = [];
  const keys = Object.keys(projections);
  if (keys.length > MAX_PROJECTED_CHANNELS) {
    issues.push(`Mais de ${MAX_PROJECTED_CHANNELS} projeções.`);
  }
  for (const key of keys) {
    if (!selectedChannels.includes(key)) {
      issues.push(`Projeção para canal não selecionado: ${key}.`);
    }
    const { unit, multiplier, offset } = projections[key];
    if (unit != null && (unit.length < 1 || unit.length > MAX_PROJECTION_UNIT_LENGTH)) {
      issues.push(`Unidade inválida em ${key}.`);
    }
    if (
      multiplier !== undefined &&
      (!Number.isFinite(multiplier) || Math.abs(multiplier) > MAX_PROJECTION_MULTIPLIER)
    ) {
      issues.push(`Multiplicador inválido em ${key}.`);
    }
    if (
      offset !== undefined &&
      (!Number.isFinite(offset) || Math.abs(offset) > MAX_PROJECTION_OFFSET)
    ) {
      issues.push(`Offset inválido em ${key}.`);
    }
  }
  return issues;
}

/**
 * Monta o `display_projections` do request de stats a partir das preferências de tela.
 * Omite os valores padrão (multiplier 1, offset 0, unit vazia) e os canais sem nenhuma
 * mudança; devolve `undefined` quando não sobra projeção, para o campo nem ser enviado.
 * Lança `RangeError` se algo violar o schema.
 */
export function buildDisplayProjections(
  selectedChannels: readonly string[],
  prefs: DisplayProjections,
): DisplayProjections | undefined {
  const issues = validateDisplayProjections(selectedChannels, prefs);
  if (issues.length > 0) throw new RangeError(issues.join(' '));

  const out: DisplayProjections = {};
  for (const [channel, pref] of Object.entries(prefs)) {
    const item: DisplayProjections[string] = {};
    if (pref.unit) item.unit = pref.unit;
    if (pref.multiplier !== undefined && pref.multiplier !== 1) item.multiplier = pref.multiplier;
    if (pref.offset !== undefined && pref.offset !== 0) item.offset = pref.offset;
    if (Object.keys(item).length > 0) out[channel] = item;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Os três estados distintos de uma célula de estatística (D8, contrato §12.1 e §14). */
export type StatCellState = 'absent' | 'null' | 'value';

/**
 * `undefined` = campo ausente na resposta; `null` = sem dado válido; número (inclusive 0
 * e NaN já lidos) = valor. Zero é valor válido.
 */
export function classifyStatCell(value: number | null | undefined): StatCellState {
  if (value === undefined) return 'absent';
  if (value === null) return 'null';
  return 'value';
}

/** Atalho para quando só importa se há número para exibir (zero conta como valor). */
export function hasStatValue(value: number | null | undefined): value is number {
  return classifyStatCell(value) === 'value';
}
